import React from 'react';
import {
  Box,
  Button,
  Checkbox,
  Chip,
  Divider,
  FormControl,
  FormControlLabel,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Rating,
  Select,
  Stack,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import StarBorderIcon from '@mui/icons-material/StarBorder';
import {
  IRatingForm,
  IRatingFormQuestion,
  RatingQuestionType,
} from '../../Interfaces/IRatingForm';

export const newRatingQuestion = (): IRatingFormQuestion => ({
  Label: '',
  QuestionType: 'stars',
  RangeMin: 1,
  RangeMax: 5,
  IsRequired: true,
  CountsForOverall: true,
  OrderNo: 0,
});

const QUESTION_TYPE_LABEL: Record<RatingQuestionType, string> = {
  stars: 'Sterne',
  scale: 'Skala',
  yesno: 'Ja / Nein',
  text: 'Freitext',
};

/**
 * Mandanten-naher Vorschau-Block einer einzelnen Frage. Da der eigentliche
 * Bogen ueber OpenFormulare ausgeliefert wird, ist dies bewusst eine
 * vereinfachte Darstellung — sie soll dem fertigen Bogen nur aehneln, damit
 * man beim Bearbeiten sieht, was der Mandant am Ende vorfindet (#1009).
 */
const QuestionPreview: React.FC<{ q: IRatingFormQuestion }> = ({ q }) => {
  if (q.QuestionType === 'stars') {
    const max = q.RangeMax && q.RangeMax > 0 ? q.RangeMax : 5;
    return (
      <Rating
        readOnly
        value={0}
        max={max}
        emptyIcon={<StarBorderIcon fontSize='inherit' />}
      />
    );
  }
  if (q.QuestionType === 'scale') {
    const min = q.RangeMin ?? 1;
    const max = q.RangeMax ?? 10;
    const span = max - min;
    // Bei kleinen Skalen alle Werte als Chips zeigen, sonst nur die Endpunkte.
    const values =
      span >= 0 && span <= 10
        ? Array.from({ length: span + 1 }, (_, i) => min + i)
        : [min, max];
    return (
      <Stack direction='row' spacing={0.5} sx={{ flexWrap: 'wrap', gap: 0.5 }}>
        {values.map((v, i) =>
          span > 10 && i === 1 ? (
            <React.Fragment key={v}>
              <Typography variant='body2' color='text.secondary'>
                …
              </Typography>
              <Chip
                key={`${v}-end`}
                size='small'
                variant='outlined'
                label={v}
              />
            </React.Fragment>
          ) : (
            <Chip key={v} size='small' variant='outlined' label={v} />
          )
        )}
      </Stack>
    );
  }
  if (q.QuestionType === 'yesno') {
    return (
      <Stack direction='row' spacing={1}>
        <Button size='small' variant='outlined' disabled>
          Ja
        </Button>
        <Button size='small' variant='outlined' disabled>
          Nein
        </Button>
      </Stack>
    );
  }
  return (
    <TextField
      size='small'
      fullWidth
      disabled
      multiline
      minRows={2}
      placeholder='Freitext-Antwort des Mandanten …'
    />
  );
};

interface IProps {
  value: IRatingForm;
  onChange: (next: IRatingForm) => void;
}

export const RatingFormStructureEditor: React.FC<IProps> = ({
  value,
  onChange,
}) => {
  const patch = (p: Partial<IRatingForm>) => onChange({ ...value, ...p });

  const updateQuestion = (idx: number, qp: Partial<IRatingFormQuestion>) => {
    const next = [...(value.Questions ?? [])];
    next[idx] = { ...next[idx], ...qp };
    patch({ Questions: next });
  };

  const moveQuestion = (idx: number, dir: -1 | 1) => {
    const list = [...(value.Questions ?? [])];
    const target = idx + dir;
    if (target < 0 || target >= list.length) return;
    [list[idx], list[target]] = [list[target], list[idx]];
    patch({ Questions: list });
  };

  const removeQuestion = (idx: number) => {
    const list = [...(value.Questions ?? [])];
    list.splice(idx, 1);
    patch({ Questions: list });
  };

  const addQuestion = () =>
    patch({ Questions: [...(value.Questions ?? []), newRatingQuestion()] });

  return (
    <Stack spacing={3}>
      <Grid container spacing={2}>
        <Grid item xs={12} md={8}>
          <TextField
            label='Titel'
            fullWidth
            required
            value={value.Title}
            onChange={e => patch({ Title: e.target.value })}
          />
        </Grid>
        <Grid item xs={6} md={2}>
          <FormControlLabel
            control={
              <Switch
                checked={!!value.IsActive}
                onChange={(_, v) => patch({ IsActive: v })}
              />
            }
            label='Aktiv'
          />
        </Grid>
        <Grid item xs={6} md={2}>
          <FormControlLabel
            control={
              <Switch
                checked={!!value.IsDefault}
                onChange={(_, v) => patch({ IsDefault: v })}
              />
            }
            label='Standard'
          />
        </Grid>
      </Grid>

      <Divider />

      <Box>
        <Stack
          direction='row'
          justifyContent='space-between'
          alignItems='center'
          sx={{ mb: 1 }}
        >
          <Typography variant='subtitle1'>Fragen</Typography>
          <Button size='small' startIcon={<AddIcon />} onClick={addQuestion}>
            Frage hinzufügen
          </Button>
        </Stack>
        <Stack spacing={2}>
          {(value.Questions ?? []).map((q, idx) => (
            <Box
              key={idx}
              sx={{
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 1,
                p: 2,
              }}
            >
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <TextField
                    label='Bezeichnung'
                    fullWidth
                    value={q.Label}
                    onChange={e =>
                      updateQuestion(idx, { Label: e.target.value })
                    }
                  />
                </Grid>
                <Grid item xs={6} md={3}>
                  <FormControl fullWidth>
                    <InputLabel>Typ</InputLabel>
                    <Select
                      label='Typ'
                      value={q.QuestionType}
                      onChange={e => {
                        const type = e.target.value as RatingQuestionType;
                        const qp: Partial<IRatingFormQuestion> = {
                          QuestionType: type,
                        };
                        if (type === 'stars') {
                          qp.RangeMin = 1;
                          qp.RangeMax = 5;
                          qp.CountsForOverall = true;
                        } else if (type === 'scale') {
                          qp.RangeMin = 1;
                          qp.RangeMax = 10;
                          qp.CountsForOverall = true;
                        } else if (type === 'yesno') {
                          qp.RangeMin = 0;
                          qp.RangeMax = 1;
                          qp.CountsForOverall = false;
                        } else {
                          qp.RangeMin = null;
                          qp.RangeMax = null;
                          qp.CountsForOverall = false;
                        }
                        updateQuestion(idx, qp);
                      }}
                    >
                      {(['stars', 'scale', 'yesno', 'text'] as const).map(t => (
                        <MenuItem key={t} value={t}>
                          {QUESTION_TYPE_LABEL[t]}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={6} md={3}>
                  <Stack
                    direction='row'
                    justifyContent='flex-end'
                    alignItems='center'
                  >
                    <Tooltip title='Hoch'>
                      <span>
                        <IconButton
                          size='small'
                          disabled={idx === 0}
                          onClick={() => moveQuestion(idx, -1)}
                        >
                          <ArrowUpwardIcon fontSize='small' />
                        </IconButton>
                      </span>
                    </Tooltip>
                    <Tooltip title='Runter'>
                      <span>
                        <IconButton
                          size='small'
                          disabled={idx === (value.Questions?.length ?? 0) - 1}
                          onClick={() => moveQuestion(idx, 1)}
                        >
                          <ArrowDownwardIcon fontSize='small' />
                        </IconButton>
                      </span>
                    </Tooltip>
                    <Tooltip title='Entfernen'>
                      <IconButton
                        size='small'
                        color='error'
                        onClick={() => removeQuestion(idx)}
                      >
                        <DeleteOutlineIcon fontSize='small' />
                      </IconButton>
                    </Tooltip>
                  </Stack>
                </Grid>
                {(q.QuestionType === 'stars' || q.QuestionType === 'scale') && (
                  <>
                    <Grid item xs={6} md={3}>
                      <TextField
                        label='Min.'
                        type='number'
                        fullWidth
                        value={q.RangeMin ?? ''}
                        onChange={e =>
                          updateQuestion(idx, {
                            RangeMin:
                              e.target.value === ''
                                ? null
                                : Number(e.target.value),
                          })
                        }
                      />
                    </Grid>
                    <Grid item xs={6} md={3}>
                      <TextField
                        label='Max.'
                        type='number'
                        fullWidth
                        value={q.RangeMax ?? ''}
                        onChange={e =>
                          updateQuestion(idx, {
                            RangeMax:
                              e.target.value === ''
                                ? null
                                : Number(e.target.value),
                          })
                        }
                      />
                    </Grid>
                  </>
                )}
                <Grid item xs={6} md={3}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={q.IsRequired !== false}
                        onChange={(_, v) =>
                          updateQuestion(idx, { IsRequired: v })
                        }
                      />
                    }
                    label='Pflichtfeld'
                  />
                </Grid>
                <Grid item xs={6} md={3}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={q.CountsForOverall !== false}
                        disabled={
                          q.QuestionType === 'text' ||
                          q.QuestionType === 'yesno'
                        }
                        onChange={(_, v) =>
                          updateQuestion(idx, { CountsForOverall: v })
                        }
                      />
                    }
                    label='In Gesamtwert einrechnen'
                  />
                </Grid>

                {/* Mandanten-nahe Vorschau, damit der Edit-Bereich dem
                    fertigen Bogen aehnelt (#1009). */}
                <Grid item xs={12}>
                  <Box
                    sx={{
                      bgcolor: 'action.hover',
                      borderRadius: 1,
                      px: 2,
                      py: 1.5,
                    }}
                  >
                    <Typography
                      variant='body2'
                      sx={{ mb: 0.5, fontWeight: 500 }}
                    >
                      {q.Label?.trim() || 'Vorschau'}
                      {q.IsRequired !== false ? ' *' : ''}
                    </Typography>
                    <QuestionPreview q={q} />
                  </Box>
                </Grid>
              </Grid>
            </Box>
          ))}
        </Stack>
      </Box>

      <Divider />

      <Box>
        <Typography variant='subtitle1' sx={{ mb: 1 }}>
          E-Mail-Versand
        </Typography>
        <Typography
          variant='caption'
          color='text.secondary'
          sx={{ display: 'block', mb: 2 }}
        >
          Die Vorlagen für Einladung und Eingangsbestätigung sind fest und
          werden zentral in „Systemnachrichten → Bewertungsbögen" gepflegt.
        </Typography>
        <FormControlLabel
          control={
            <Switch
              checked={!!value.ConfirmationEnabled}
              onChange={(_, v) => patch({ ConfirmationEnabled: v })}
            />
          }
          label='Eingangsbestätigung an den Mandanten senden'
        />
      </Box>

      <Divider />

      <Box>
        <Typography variant='subtitle1' sx={{ mb: 1 }}>
          Dankeschön / Bewertungs-Anstoß
        </Typography>
        <Typography
          variant='caption'
          color='text.secondary'
          sx={{ display: 'block', mb: 2 }}
        >
          Der <strong>Dankestext</strong> wird dem Mandanten nach Abgabe der
          Bewertung angezeigt und – sofern oben die Eingangsbestätigung
          aktiviert ist – als Platzhalter <code>{'{RatingThanksText}'}</code> in
          die Bestätigungs-E-Mail eingefügt. Der{' '}
          <strong>Bewertungs-Link</strong> (Linktext + Link-URL) erscheint dem
          Mandanten zusätzlich nur dann, wenn der Gesamtwert seiner Bewertung
          den Schwellenwert erreicht oder überschreitet.
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={12} md={4}>
            <TextField
              label='Schwellenwert (≥)'
              type='number'
              fullWidth
              helperText='Ab diesem Gesamtwert wird der Link angezeigt.'
              value={value.ThresholdScore ?? ''}
              onChange={e =>
                patch({
                  ThresholdScore:
                    e.target.value === '' ? null : Number(e.target.value),
                })
              }
            />
          </Grid>
          <Grid item xs={12} md={8}>
            <TextField
              label='Linktext'
              fullWidth
              value={value.ThanksLinkLabel ?? ''}
              onChange={e => patch({ ThanksLinkLabel: e.target.value })}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              label='Link-URL (z. B. Google-Bewertung)'
              fullWidth
              value={value.ThanksLinkUrl ?? ''}
              onChange={e => patch({ ThanksLinkUrl: e.target.value })}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              label='Dankestext'
              fullWidth
              multiline
              minRows={3}
              value={value.ThanksText ?? ''}
              onChange={e => patch({ ThanksText: e.target.value })}
            />
          </Grid>
        </Grid>
      </Box>
    </Stack>
  );
};
